/** Komunikace s API hubu. CSRF token je v cookie `hub_csrf` (SPEC kap. 12). */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

function csrfToken(): string {
  const match = /(?:^|;\s*)hub_csrf=([^;]+)/.exec(document.cookie);
  return match?.[1] ?? '';
}

function popis(status: number, code?: string): string {
  if (status === 401) return 'Nejste přihlášeni.';
  if (status === 403) return 'Na tuto akci nemáte právo.';
  if (status === 404) return 'Nenalezeno.';
  if (status === 409) return 'Akci nelze provést v tomhle stavu.';
  if (status >= 500) return 'Chyba serveru. Zkuste to znovu.';
  return code ?? 'Něco se nepovedlo.';
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  /* Čtení nesmí viset donekonečna: po 60 s se ukáže chyba místo věčného „Načítám“. */
  const res = await fetch(path, {
    method,
    ...(method === 'GET' ? { signal: AbortSignal.timeout(60_000) } : {}),
    credentials: 'same-origin',
    headers: {
      ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      ...(method === 'GET' ? {} : { 'x-csrf-token': csrfToken() }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  }).catch((e: unknown) => {
    if (e instanceof DOMException && e.name === 'TimeoutError')
      throw new ApiError(0, 'timeout', 'Server neodpověděl včas (60 s). Zkuste to znovu.');
    throw e;
  });

  const text = await res.text().catch(() => {
    throw new ApiError(0, 'timeout', 'Server neodpověděl včas (60 s). Zkuste to znovu.');
  });
  const parsed: unknown = text.length > 0 ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = parsed as { error?: string; message?: string } | null;
    throw new ApiError(
      res.status,
      err?.error ?? 'error',
      err?.message ?? popis(res.status, err?.error),
    );
  }
  return parsed as T;
}

export const api = {
  get: <T>(path: string): Promise<T> => call<T>('GET', path),
  post: <T>(path: string, body?: unknown): Promise<T> => call<T>('POST', path, body ?? {}),
  patch: <T>(path: string, body?: unknown): Promise<T> => call<T>('PATCH', path, body ?? {}),
  put: <T>(path: string, body?: unknown): Promise<T> => call<T>('PUT', path, body ?? {}),
  del: <T>(path: string): Promise<T> => call<T>('DELETE', path),
};

export const qs = (params: Record<string, string | number | undefined | null>): string => {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value));
  }
  const text = search.toString();
  return text.length > 0 ? `?${text}` : '';
};
